import { useState, type ComponentProps, useEffect, useCallback } from 'react'
import { Input, Popover, PopoverContent, PopoverTrigger } from '@bpinternal/ui-kit-next'
import { cn } from '~/utils'
import { apcaContrast } from '@bpinternal/shared'
import { HexColorPopover } from './HexColorPopover'

type Props = Omit<ComponentProps<typeof Input>, 'onChange'> & {
  onChange?: (color: string) => void
  dashboardTheme?: 'light' | 'dark'
}

export const HexColorInput = ({ value, onChange, dashboardTheme = 'light', ...props }: Props) => {
  const [inputValue, setInputValue] = useState(value || '#000000')
  const escapeChars = useCallback((val: string) => val.replace(/([^0-9A-F]+)/gi, '').substring(0, 6), [])

  useEffect(() => {
    setInputValue(String(value).replace('#', '') || '')
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = escapeChars(e.target.value.trim())

    // Basic validation for hex color format (# followed by 3 or 6 hexadecimal digits)
    const isValidHex = /^#([0-9A-Fa-f]{3}){1,2}$/.test(`#${input}`)

    setInputValue(input.replace('#', ''))
    if (isValidHex) {
      if (onChange) {
        onChange(`#${input}`) // pass the event to parent handler if valid
      }
    }
  }

  return (
    <div className="relative">
      <Popover>
        <PopoverTrigger asChild>
          <div
            className={cn(
              'absolute left-2 top-1/2 h-6 w-6 -translate-y-1/2 cursor-pointer rounded-lg border-2 border-white shadow-md transition-all hover:shadow-lg dark:border-black'
            )}
            style={{
              background: `${value}`,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          />
        </PopoverTrigger>
        <PopoverContent className="w-min p-3">
          <HexColorPopover color={(value as string) || ''} setColor={onChange || (() => {})} />
        </PopoverContent>
      </Popover>

      <Input {...props} value={'#' + inputValue} onChange={handleChange} className={cn('pl-9', props.className)} />
    </div>
  )
}
